import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { SectionCard, formatCurrency, formatNumber } from '../../../components/design-system/admin';
import type { OverviewResponse } from '../../../common/models/admin-overview.model';

function Row({ label, value, href, alert }: { label: string; value: ReactNode; href: string; alert?: boolean }) {
  return (
    <li>
      <Link to={href} className="group flex items-center justify-between gap-3 py-2 text-sm transition-colors hover:text-foreground">
        <span className="text-muted-foreground group-hover:text-foreground">{label}</span>
        <span className="flex items-center gap-1">
          <span className={cn('font-medium tabular-nums', alert && 'text-destructive')}>{value}</span>
          <ChevronRight className="size-3.5 text-muted-foreground rtl:rotate-180" />
        </span>
      </Link>
    </li>
  );
}

/** Support, sales and plan mix at a glance, each a door to its page. */
export function DeskCard({ data, className }: { data: OverviewResponse; className?: string }) {
  const { t } = useTranslation();
  const { support, sales, plans } = data;
  const totalVendors = plans.reduce((n, p) => n + p.vendors, 0) || 1;

  return (
    <SectionCard title={t('ADMIN_OVERVIEW.DESK.TITLE')} className={className} testId="overview-desk">
      <ul className="-my-2 divide-y">
        <Row label={t('ADMIN_OVERVIEW.DESK.UNREAD')} value={formatNumber(support.unread)} href="/support" alert={support.unread > 0} />
        <Row label={t('ADMIN_OVERVIEW.DESK.OPEN')} value={formatNumber(support.open + support.waiting)} href="/support" />
        <Row label={t('ADMIN_OVERVIEW.DESK.TASKS')} value={formatNumber(support.openTasks)} href="/support/board" />
        <Row label={t('ADMIN_OVERVIEW.DESK.QUOTES')} value={formatNumber(sales.quotesOpen)} href="/quote-requests" />
        <Row
          label={t('ADMIN_OVERVIEW.DESK.INVOICES')}
          value={sales.invoicesOverdue ? `${formatNumber(sales.invoicesPending)} · ${formatCurrency(sales.overdueAmount, sales.currency, 0)}` : formatNumber(sales.invoicesPending)}
          href="/wire-invoices"
          alert={sales.invoicesOverdue > 0}
        />
        <Row label={t('ADMIN_OVERVIEW.DESK.TEAM_TESTING')} value={formatNumber(data.health.teamTesting.events)} href="/team-devices" />
      </ul>

      <div className="mt-5">
        <p className="mb-2 text-xs font-medium text-muted-foreground">{t('ADMIN_OVERVIEW.PLANS.DESCRIPTION')}</p>
        <div className="flex h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
          {plans.map((p, i) => (
            <div key={p.id ?? 'none'} className="h-full" style={{ width: `${(p.vendors / totalVendors) * 100}%`, background: 'var(--foreground)', opacity: [0.8, 0.5, 0.3, 0.15][i % 4] }} />
          ))}
        </div>
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {plans.map((p, i) => (
            <li key={p.id ?? 'none'} className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-foreground" style={{ opacity: [0.8, 0.5, 0.3, 0.15][i % 4] }} aria-hidden />
              <span className="text-muted-foreground">{p.name || t('ADMIN_OVERVIEW.PLANS.NO_PLAN')}</span>
              <span className="font-medium tabular-nums">{p.vendors}</span>
            </li>
          ))}
        </ul>
      </div>
    </SectionCard>
  );
}
